import React, { useState, useEffect } from 'react';
import { lawApi } from '../../lawApi';
import { useTheme } from '../../components/LawPortalLayout';
import { Plus, Trash2, ChevronDown, ChevronUp, GraduationCap, Loader2 } from 'lucide-react';
import { toast } from 'sonner';

const CATEGORIES = { reach: { label: 'Reach', color: 'text-red-500 bg-red-50 border-red-200' }, target: { label: 'Target', color: 'text-blue-500 bg-blue-50 border-blue-200' }, likely: { label: 'Likely', color: 'text-emerald-500 bg-emerald-50 border-emerald-200' } };
const RESULTS = ['admitted', 'waitlisted', 'denied', 'withdrawn'];

export default function LawSchools() {
  const [schools, setSchools] = useState([]);
  const [loading, setLoading] = useState(true);
  const [adding, setAdding] = useState(false);
  const [expanded, setExpanded] = useState(null);
  const [form, setForm] = useState({ name: '', category: 'target', deadline: '', deadline_type: 'RD', median_lsat: '', median_gpa: '' });
  const theme = useTheme();
  const { dark, textPrimary, textSecondary, textMuted, borderColor, hoverBg } = theme;
  const cardBg = dark ? 'bg-[#141618]' : 'bg-white';
  const inputClass = `w-full h-8 px-2.5 text-xs rounded border ${borderColor} ${dark ? 'bg-[#1C1E20] text-[#F0EFEC]' : 'bg-white text-[#1A1A1A]'} focus:outline-none focus:ring-1 focus:ring-[#C5A55A]/50`;

  const load = () => lawApi.getSchools().then(setSchools).catch(() => toast.error('Failed to load')).finally(() => setLoading(false));
  useEffect(() => { load(); }, []);

  const handleAdd = async (e) => {
    e.preventDefault();
    if (!form.name.trim()) return;
    try {
      await lawApi.createSchool({
        ...form,
        median_lsat: form.median_lsat ? parseInt(form.median_lsat) : null,
        median_gpa: form.median_gpa ? parseFloat(form.median_gpa) : null,
      });
      setForm({ name: '', category: 'target', deadline: '', deadline_type: 'RD', median_lsat: '', median_gpa: '' });
      setAdding(false);
      load();
      toast.success('School added');
    } catch (e) { toast.error(e.message); }
  };

  const handleUpdate = async (id, field, value) => {
    try {
      await lawApi.updateSchool(id, { [field]: value });
      load();
    } catch (e) { toast.error(e.message); }
  };

  const handleDelete = async (id, name) => {
    if (!window.confirm(`Remove "${name}"? This also deletes its application and essays.`)) return;
    try { await lawApi.deleteSchool(id); load(); toast.success('Removed'); } catch (e) { toast.error(e.message); }
  };

  const sorted = [...schools].sort((a, b) => {
    const order = { reach: 0, target: 1, likely: 2 };
    return (order[a.category] || 1) - (order[b.category] || 1);
  });

  if (loading) return <div className={`h-64 animate-pulse rounded-lg ${dark ? 'bg-[#1C1E20]' : 'bg-[#E8E6E1]'}`} />;

  return (
    <div className="space-y-5" data-testid="law-schools">
      <div className="flex items-center justify-between">
        <div>
          <h1 className={`text-lg font-semibold ${textPrimary}`} style={{ fontFamily: "'Inter', sans-serif" }}>School List</h1>
          <p className={`text-[11px] ${textMuted} mt-0.5`}>{schools.length} school{schools.length !== 1 ? 's' : ''} · {schools.filter(s => s.category === 'reach').length}R · {schools.filter(s => s.category === 'target').length}T · {schools.filter(s => s.category === 'likely').length}L</p>
        </div>
        <button onClick={() => setAdding(!adding)}
          className="flex items-center gap-1.5 px-3 py-1.5 bg-[#1A1A1A] text-white text-xs rounded hover:bg-[#333] transition-colors"
          data-testid="law-add-school">
          <Plus className="h-3.5 w-3.5" /> Add School
        </button>
      </div>

      {/* Add Form */}
      {adding && (
        <form onSubmit={handleAdd} className={`${cardBg} border ${borderColor} rounded-lg p-4 space-y-3`}>
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
            <div className="col-span-2">
              <input className={inputClass} placeholder="School name *" value={form.name} onChange={e => setForm({ ...form, name: e.target.value })} autoFocus data-testid="law-school-name-input" />
            </div>
            <select className={inputClass} value={form.category} onChange={e => setForm({ ...form, category: e.target.value })}>
              <option value="reach">Reach</option><option value="target">Target</option><option value="likely">Likely</option>
            </select>
            <select className={inputClass} value={form.deadline_type} onChange={e => setForm({ ...form, deadline_type: e.target.value })}>
              <option value="ED">Early Decision</option><option value="EA">Early Action</option><option value="RD">Regular Decision</option><option value="rolling">Rolling</option>
            </select>
            <input className={inputClass} type="date" placeholder="Deadline" value={form.deadline} onChange={e => setForm({ ...form, deadline: e.target.value })} />
            <input className={inputClass} type="number" placeholder="Median LSAT" value={form.median_lsat} onChange={e => setForm({ ...form, median_lsat: e.target.value })} />
            <input className={inputClass} type="number" step="0.01" placeholder="Median GPA" value={form.median_gpa} onChange={e => setForm({ ...form, median_gpa: e.target.value })} />
            <button type="submit" className="h-8 px-4 bg-[#1A1A1A] text-white text-xs rounded hover:bg-[#333]" data-testid="law-school-submit">Add</button>
          </div>
        </form>
      )}

      {/* Empty */}
      {schools.length === 0 && !adding && (
        <div className={`${cardBg} border ${borderColor} rounded-lg p-8 text-center`}>
          <GraduationCap className={`h-8 w-8 ${textMuted} mx-auto mb-2`} strokeWidth={1} />
          <p className={`text-sm ${textSecondary}`}>No schools added yet</p>
          <p className={`text-xs ${textMuted} mt-1`}>Click "Add School" to build your list</p>
        </div>
      )}

      {/* School List */}
      <div className="space-y-2">
        {sorted.map(school => {
          const cat = CATEGORIES[school.category] || CATEGORIES.target;
          const isExpanded = expanded === school.id;
          return (
            <div key={school.id} className={`${cardBg} border ${borderColor} rounded-lg overflow-hidden`} data-testid={`law-school-${school.id}`}>
              <button onClick={() => setExpanded(isExpanded ? null : school.id)}
                className={`w-full flex items-center gap-3 px-4 py-3 text-left ${hoverBg} transition-colors`}>
                <span className={`px-2 py-0.5 text-[9px] font-semibold uppercase rounded border ${cat.color}`}>{cat.label}</span>
                <span className={`text-sm font-medium flex-1 ${textPrimary}`}>{school.name}</span>
                {school.median_lsat && <span className={`text-[10px] ${textMuted}`}>LSAT {school.median_lsat}</span>}
                {school.median_gpa && <span className={`text-[10px] ${textMuted}`}>GPA {school.median_gpa}</span>}
                {school.deadline && <span className={`text-[10px] font-mono ${textSecondary}`}>{school.deadline}</span>}
                {school.result && <span className={`px-2 py-0.5 text-[9px] rounded ${school.result === 'admitted' ? 'bg-emerald-100 text-emerald-700' : school.result === 'denied' ? 'bg-red-100 text-red-700' : 'bg-amber-100 text-amber-700'}`}>{school.result}</span>}
                {isExpanded ? <ChevronUp className={`h-4 w-4 ${textMuted}`} /> : <ChevronDown className={`h-4 w-4 ${textMuted}`} />}
              </button>
              {isExpanded && (
                <div className={`px-4 pb-4 border-t ${borderColor} pt-3 space-y-3`}>
                  <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
                    <div><label className={`text-[10px] ${textMuted}`}>Category</label>
                      <select className={inputClass} value={school.category} onChange={e => handleUpdate(school.id, 'category', e.target.value)}>
                        <option value="reach">Reach</option><option value="target">Target</option><option value="likely">Likely</option>
                      </select></div>
                    <div><label className={`text-[10px] ${textMuted}`}>Deadline</label>
                      <input className={inputClass} type="date" value={school.deadline || ''} onChange={e => handleUpdate(school.id, 'deadline', e.target.value)} /></div>
                    <div><label className={`text-[10px] ${textMuted}`}>Result</label>
                      <select className={inputClass} value={school.result || ''} onChange={e => handleUpdate(school.id, 'result', e.target.value || null)}>
                        <option value="">Pending</option>{RESULTS.map(r => <option key={r} value={r} className="capitalize">{r}</option>)}
                      </select></div>
                    <div><label className={`text-[10px] ${textMuted}`}>Scholarship</label>
                      <input className={inputClass} value={school.scholarship_notes || ''} onChange={e => handleUpdate(school.id, 'scholarship_notes', e.target.value)} placeholder="Amount / notes" /></div>
                  </div>
                  <div><label className={`text-[10px] ${textMuted}`}>Why This School</label>
                    <textarea className={`w-full px-2.5 py-2 text-xs rounded border ${borderColor} ${dark ? 'bg-[#1C1E20] text-[#F0EFEC]' : 'bg-white text-[#1A1A1A]'} focus:outline-none focus:ring-1 focus:ring-[#C5A55A]/50`}
                      value={school.why_this_school || ''} onChange={e => handleUpdate(school.id, 'why_this_school', e.target.value)} rows={2} placeholder="Positioning notes..." /></div>
                  <div className="flex justify-end">
                    <button onClick={() => handleDelete(school.id, school.name)} className="text-[10px] text-red-500 hover:text-red-400 flex items-center gap-1">
                      <Trash2 className="h-3 w-3" /> Remove
                    </button>
                  </div>
                </div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}
